<script lang="ts">
	import {
		BaseInput,
		Button,
		ButtonType,
		ButtonSize,
		DetailedSelect,
		DateRangePicker,
		Icon,
		MarkdownInput,
		Textarea,
		Checkbox,
		CheckboxSize
	} from '@repo/commons/components'
	import {
		type FormField,
		FormFieldType
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import type { DetailedSelectItem, DateSelection } from '@repo/commons/models'
	import {
		type FormSchema,
		type FormData,
		type FormErrors,
		initializeFormData,
		getFieldId,
		getFieldLabel,
		getSelectItems,
		getSelectedSelectItem,
		getSelectedSelectItems,
		shouldShowMultipleSelection,
		getMaxSelections,
		validateField,
		validateForm
	} from './dynamicFormUtils'

	interface Props {
		schema: FormSchema
		submitText?: string
		className?: string
		formClassName?: string
		locale?: string
		onSubmit?: (data: FormData) => void
		onError?: (errors: FormErrors) => void
	}

	let {
		schema,
		submitText = 'Submit',
		className = '',
		formClassName = '',
		locale = 'en',
		onSubmit,
		onError
	}: Props = $props()

	let formData: FormData = $state({})
	let errors: FormErrors = $state({})

	$effect(() => {
		formData = initializeFormData(schema)
	})

	function handleFieldChange(fieldId: string, value: any) {
		formData = { ...formData, [fieldId]: value }

		if (errors[fieldId]) {
			errors = { ...errors, [fieldId]: '' }
		}
	}

	function handleFieldBlur(field: FormField) {
		const fieldId = getFieldId(field)
		const error = validateField(field, formData[fieldId])
		errors = { ...errors, [fieldId]: error }
	}

	function handleSelectChange(field: FormField, selectedItem: DetailedSelectItem | null) {
		const fieldId = getFieldId(field)
		const value = selectedItem?.id || null

		formData = { ...formData, [fieldId]: value }

		const error = validateField(field, value)
		errors = { ...errors, [fieldId]: error }
	}

	function handleMultiSelectChange(field: FormField, selectedItems: DetailedSelectItem[]) {
		const fieldId = getFieldId(field)
		const values = selectedItems.map((item) => item.id)

		formData = { ...formData, [fieldId]: values }

		const error = validateField(field, values)
		errors = { ...errors, [fieldId]: error }
	}

	function handleDateChange(field: FormField, dateValue: DateSelection | undefined) {
		const fieldId = getFieldId(field)
		formData = { ...formData, [fieldId]: dateValue.date.toString() }

		const error = validateField(field, dateValue)
		errors = { ...errors, [fieldId]: error }
	}

	function handleCheckboxChange(field: FormField, checked: boolean) {
		const fieldId = getFieldId(field)
		handleFieldChange(fieldId, checked)
	}

	function handleSubmit(e: Event) {
		e.preventDefault()

		const { isValid, errors: validationErrors } = validateForm(schema, formData)
		errors = validationErrors

		if (isValid) {
			onSubmit?.(formData)
		} else {
			onError?.(errors)
		}
	}
</script>

<div class={`dynamic-form ${className}`}>
	<form onsubmit={handleSubmit} class={formClassName}>
		{#each schema.fields as field (getFieldId(field))}
			{@const fieldLabel = getFieldLabel(field, locale)}
			{@const isRequired = field.required}
			{@const fieldId = getFieldId(field)}

			<div class="field-wrapper">
				{#if field.formFieldType === FormFieldType.Text}
					<BaseInput
						id={fieldId}
						size="md"
						type="text"
						label={fieldLabel}
						helperText={field.hint}
						showError={!!errors[fieldId]}
						errorMessage={errors[fieldId]}
						placeholder=""
						value={formData[fieldId]}
						clearable
						onInput={(value) => handleFieldChange(fieldId, value)}
						onBlur={() => handleFieldBlur(field)}
					/>
				{:else if field.formFieldType === FormFieldType.TextArea}
					<Textarea
						id={fieldId}
						label={fieldLabel}
						helperText={field.hint}
						showError={!!errors[fieldId]}
						errorMessage={errors[fieldId]}
						value={formData[fieldId]}
						onInput={(value) => handleFieldChange(fieldId, value)}
						onBlur={() => handleFieldBlur(field)}
					/>
				{:else if field.formFieldType === FormFieldType.Checkbox}
					<div class="field-group">
						<Checkbox
							id={fieldId}
							label={fieldLabel}
							hint={field.hint}
							required={isRequired}
							error={errors[fieldId]}
							size={CheckboxSize.MEDIUM}
							checked={!!formData[fieldId]}
							onchange={(checked) => handleCheckboxChange(field, checked)}
							onblur={() => handleFieldBlur(field)}
						/>
					</div>
				{:else if field.formFieldType === FormFieldType.ExclusiveSelect}
					<DetailedSelect
						id={fieldId}
						label={fieldLabel}
						items={getSelectItems(field)}
						selectedItem={getSelectedSelectItem(field, formData)}
						selectedItems={getSelectedSelectItems(field, formData)}
						placeholder="Select an option"
						clearable
						searchable={getSelectItems(field).length > 5}
						multiSelect={shouldShowMultipleSelection(field)}
						maxSelections={getMaxSelections(field)}
						onSelect={(item) => handleSelectChange(field, item)}
						onMultiSelect={(items) => handleMultiSelectChange(field, items)}
						onBlur={() => handleFieldBlur(field)}
						error={errors[fieldId]}
					/>
				{:else if field.formFieldType === FormFieldType.DatePicker}
					<DateRangePicker
						label={fieldLabel}
						helperText={field.hint}
						mode="single"
						value={formData[fieldId]}
						onChange={(value) => handleDateChange(field, value)}
						placeholder="Select a date"
						size="md"
						showError={!!errors[fieldId]}
						errorMessage={errors[fieldId]}
					/>
				{:else if field.formFieldType === FormFieldType.TimePicker}
					<BaseInput
						id={fieldId}
						type="time"
						size="md"
						label={fieldLabel}
						helperText={field.hint}
						showError={!!errors[fieldId]}
						errorMessage={errors[fieldId]}
						value={formData[fieldId]}
						onInput={(value) => handleFieldChange(fieldId, value)}
						onBlur={() => handleFieldBlur(field)}
						append={{
							component: Icon,
							props: { name: 'clock', size: 'sm', class: 'text-[#002C4D80]' }
						}}
					/>
				{:else if field.formFieldType === FormFieldType.DateTimePicker}
					<DateRangePicker
						label={fieldLabel}
						helperText={field.hint}
						mode="single"
						enableTimeSelection={true}
						value={formData[fieldId]}
						onChange={(value) => handleDateChange(field, value)}
						placeholder="Select date and time"
						size="md"
						showError={!!errors[fieldId]}
						errorMessage={errors[fieldId]}
					/>
				{:else if field.formFieldType === FormFieldType.DateRangePicker}
					<DateRangePicker
						label={fieldLabel}
						helperText={field.hint}
						mode="range"
						value={formData[fieldId]}
						onChange={(value) => handleDateChange(field, value)}
						placeholder="Select date range"
						size="md"
						showError={!!errors[fieldId]}
						errorMessage={errors[fieldId]}
					/>
				{:else if field.formFieldType === FormFieldType.DateTimeRangePicker}
					<DateRangePicker
						label={fieldLabel}
						helperText={field.hint}
						mode="range"
						enableTimeSelection={true}
						value={formData[fieldId]}
						onChange={(value) => handleDateChange(field, value)}
						placeholder="Select date and time range"
						size="md"
						showError={!!errors[fieldId]}
						errorMessage={errors[fieldId]}
					/>
				{:else if field.formFieldType === FormFieldType.TimeRangePicker}
					<div class="field-group">
						<div class="time-range-wrapper">
							<BaseInput
								size="md"
								label={fieldLabel}
								helperText={field.hint}
								type="time"
								placeholder="Start time"
								value={formData[fieldId]?.start || ''}
								onInput={(value) =>
									handleFieldChange(fieldId, { ...formData[fieldId], start: value })}
								onBlur={() => handleFieldBlur(field)}
							/>
							<BaseInput
								label="-"
								helperText="-"
								size="md"
								type="time"
								placeholder="End time"
								value={formData[fieldId]?.end || ''}
								onInput={(value) =>
									handleFieldChange(fieldId, { ...formData[fieldId], end: value })}
								onBlur={() => handleFieldBlur(field)}
							/>
						</div>
						{#if errors[fieldId]}
							<p class="field-error">{errors[fieldId]}</p>
						{/if}
					</div>
				{:else if field.formFieldType === FormFieldType.ReadOnlyMdText}
					<div class="field-group">
						{#if fieldLabel}
							<MarkdownInput readonly pure value={fieldLabel} defaultMode="preview" fitHeight />
						{/if}
					</div>
				{:else}
					<!-- Fallback for unsupported field types -->
					<div class="field-group">
						<p class="unsupported-field">
							Unsupported field type: {field.formFieldType}
						</p>
					</div>
				{/if}
			</div>
		{/each}

		<div class="submit-button">
			<Button
				type="submit"
				fillWidth
				buttonText={submitText}
				buttonType={ButtonType.POSITIVE}
				buttonSize={ButtonSize.LARGE}
			/>
		</div>
	</form>
</div>

<style>
	.dynamic-form {
		@apply w-full max-w-4xl mx-auto;
	}

	.field-wrapper {
		@apply w-full;
	}

	.submit-button {
		@apply mt-6;
	}

	.field-group {
		@apply space-y-3;
	}

	.field-error {
		@apply text-sm text-red-600 mt-1;
	}

	.unsupported-field {
		@apply text-amber-700 bg-amber-50 p-4 rounded-lg border border-amber-200 font-medium;
	}

	.time-range-wrapper {
		@apply w-full flex items-center gap-4;
	}
</style>
